import React, { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { useAuth } from '../contexts/AuthContext';
import { auditAPI } from '../utils/api';
import { AuditLog, AuditLogFilters } from '../types';
import {
  MagnifyingGlassIcon,
  FunnelIcon,
  ChevronLeftIcon,
  ChevronRightIcon,
  XMarkIcon,
  ClockIcon,
  UserIcon,
  DocumentTextIcon,
  ComputerDesktopIcon,
  BuildingOfficeIcon,
} from '@heroicons/react/24/outline';
import { ACTION_COLORS, ACTION_LABELS, RESOURCE_LABELS } from '../utils/constants';

const AuditLogs: React.FC = () => {
  const { user } = useAuth();
  const isHost = user?.is_host || false;

  const [filters, setFilters] = useState<AuditLogFilters>({
    page: 1,
    limit: 20,
  });
  const [showFilters, setShowFilters] = useState(false);
  const [selectedLog, setSelectedLog] = useState<AuditLog | null>(null);

  // Fetch audit logs
  const { data: logsData, isLoading } = useQuery({
    queryKey: ['auditLogs', filters],
    queryFn: async () => {
      const response = await auditAPI.getLogs(filters);
      return response.data;
    },
  });

  // Fetch filter options
  const { data: actionsData } = useQuery({
    queryKey: ['auditActions'],
    queryFn: async () => {
      const response = await auditAPI.getActions();
      return response.data.data || [];
    },
  });

  const { data: resourceTypesData } = useQuery({
    queryKey: ['auditResourceTypes'],
    queryFn: async () => {
      const response = await auditAPI.getResourceTypes();
      return response.data.data || [];
    },
  });

  // Fetch stats
  const { data: statsData } = useQuery({
    queryKey: ['auditStats'],
    queryFn: async () => {
      const response = await auditAPI.getStats(30);
      return response.data.data;
    },
  });

  const logs = logsData?.data || [];
  const pagination = logsData?.pagination;
  const actions = actionsData || [];
  const resourceTypes = resourceTypesData || [];

  const handleFilterChange = (key: keyof AuditLogFilters, value: any) => {
    setFilters((prev) => ({
      ...prev,
      [key]: value,
      page: key !== 'page' ? 1 : value, // Reset page when other filters change
    }));
  };

  const clearFilters = () => {
    setFilters({ page: 1, limit: 20 });
  };

  const formatDate = (dateString: string) => {
    const date = new Date(dateString);
    return new Intl.DateTimeFormat('it-IT', {
      dateStyle: 'medium',
      timeStyle: 'short',
    }).format(date);
  };

  const getActionBadge = (action: string) => {
    const colorClass = ACTION_COLORS[action] || 'bg-gray-100 text-gray-800';
    const label = ACTION_LABELS[action] || action;
    return (
      <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${colorClass}`}>
        {label}
      </span>
    );
  };

  const getResourceLabel = (resourceType: string) => {
    return RESOURCE_LABELS[resourceType] || resourceType;
  };

  const renderChanges = (oldValues: Record<string, any> | undefined, newValues: Record<string, any> | undefined) => {
    if (!oldValues && !newValues) return null;

    const allKeys = new Set([
      ...Object.keys(oldValues || {}),
      ...Object.keys(newValues || {}),
    ]);

    return (
      <div className="mt-4 space-y-2">
        <h4 className="text-sm font-medium text-gray-900">Modifiche</h4>
        <div className="bg-gray-50 rounded-lg p-3 text-sm space-y-2 max-h-60 overflow-y-auto">
          {Array.from(allKeys).map((key) => {
            const oldVal = oldValues?.[key];
            const newVal = newValues?.[key];
            if (oldVal === newVal) return null;

            return (
              <div key={key} className="border-b border-gray-200 pb-2 last:border-0 last:pb-0">
                <span className="font-medium text-gray-700">{key}:</span>
                <div className="ml-2 grid grid-cols-2 gap-2 mt-1">
                  {oldVal !== undefined && (
                    <div className="text-red-600">
                      <span className="text-xs text-gray-500">Prima: </span>
                      <span className="font-mono text-xs break-all">
                        {typeof oldVal === 'object' ? JSON.stringify(oldVal) : String(oldVal)}
                      </span>
                    </div>
                  )}
                  {newVal !== undefined && (
                    <div className="text-green-600">
                      <span className="text-xs text-gray-500">Dopo: </span>
                      <span className="font-mono text-xs break-all">
                        {typeof newVal === 'object' ? JSON.stringify(newVal) : String(newVal)}
                      </span>
                    </div>
                  )}
                </div>
              </div>
            );
          })}
        </div>
      </div>
    );
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex justify-between items-center">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Registro Attivita</h1>
          <p className="text-gray-600 mt-1">
            {isHost
              ? 'Visualizza tutte le attivita della piattaforma'
              : 'Visualizza le attivita del tuo tenant'}
          </p>
        </div>
      </div>

      {/* Stats Cards */}
      {statsData && (
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
          <div className="bg-white rounded-lg shadow p-4">
            <div className="text-sm font-medium text-gray-500">Totale Log</div>
            <div className="mt-1 text-2xl font-semibold text-gray-900">
              {statsData.total_logs.toLocaleString()}
            </div>
          </div>
          <div className="bg-white rounded-lg shadow p-4">
            <div className="text-sm font-medium text-gray-500">Ultimi 30 giorni</div>
            <div className="mt-1 text-2xl font-semibold text-gray-900">
              {statsData.recent_activity_count.toLocaleString()}
            </div>
          </div>
          <div className="bg-white rounded-lg shadow p-4">
            <div className="text-sm font-medium text-gray-500">Tipi di Azione</div>
            <div className="mt-1 text-2xl font-semibold text-gray-900">
              {Object.keys(statsData.actions_breakdown).length}
            </div>
          </div>
          <div className="bg-white rounded-lg shadow p-4">
            <div className="text-sm font-medium text-gray-500">Tipi di Risorsa</div>
            <div className="mt-1 text-2xl font-semibold text-gray-900">
              {Object.keys(statsData.resource_types_breakdown).length}
            </div>
          </div>
        </div>
      )}

      {/* Filters */}
      <div className="bg-white rounded-lg shadow">
        <div className="p-4 border-b border-gray-200">
          <div className="flex flex-wrap gap-4 items-center">
            {/* Search */}
            <div className="flex-1 min-w-[200px]">
              <div className="relative">
                <MagnifyingGlassIcon className="absolute left-3 top-1/2 -translate-y-1/2 h-5 w-5 text-gray-400" />
                <input
                  type="text"
                  placeholder="Cerca..."
                  value={filters.search || ''}
                  onChange={(e) => handleFilterChange('search', e.target.value || undefined)}
                  className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
                />
              </div>
            </div>

            {/* Toggle filters button */}
            <button
              onClick={() => setShowFilters(!showFilters)}
              className={`flex items-center space-x-2 px-4 py-2 border rounded-lg transition-colors ${
                showFilters
                  ? 'bg-primary-50 border-primary-500 text-primary-700'
                  : 'border-gray-300 text-gray-700 hover:bg-gray-50'
              }`}
            >
              <FunnelIcon className="h-5 w-5" />
              <span>Filtri</span>
            </button>

            {/* Clear filters */}
            {(filters.action || filters.resource_type || filters.date_from || filters.date_to || filters.search) && (
              <button
                onClick={clearFilters}
                className="flex items-center space-x-1 text-sm text-gray-500 hover:text-gray-700"
              >
                <XMarkIcon className="h-4 w-4" />
                <span>Cancella filtri</span>
              </button>
            )}
          </div>

          {/* Expanded filters */}
          {showFilters && (
            <div className="mt-4 pt-4 border-t border-gray-200 grid grid-cols-1 md:grid-cols-4 gap-4">
              {/* Action filter */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Azione
                </label>
                <select
                  value={filters.action || ''}
                  onChange={(e) => handleFilterChange('action', e.target.value || undefined)}
                  className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
                >
                  <option value="">Tutte</option>
                  {actions.map((action) => (
                    <option key={action} value={action}>
                      {ACTION_LABELS[action] || action}
                    </option>
                  ))}
                </select>
              </div>

              {/* Resource type filter */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Tipo Risorsa
                </label>
                <select
                  value={filters.resource_type || ''}
                  onChange={(e) => handleFilterChange('resource_type', e.target.value || undefined)}
                  className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
                >
                  <option value="">Tutti</option>
                  {resourceTypes.map((type) => (
                    <option key={type} value={type}>
                      {RESOURCE_LABELS[type] || type}
                    </option>
                  ))}
                </select>
              </div>

              {/* Date from */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Data da
                </label>
                <input
                  type="date"
                  value={filters.date_from?.split('T')[0] || ''}
                  onChange={(e) =>
                    handleFilterChange('date_from', e.target.value ? `${e.target.value}T00:00:00` : undefined)
                  }
                  className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
                />
              </div>

              {/* Date to */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Data a
                </label>
                <input
                  type="date"
                  value={filters.date_to?.split('T')[0] || ''}
                  onChange={(e) =>
                    handleFilterChange('date_to', e.target.value ? `${e.target.value}T23:59:59` : undefined)
                  }
                  className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
                />
              </div>
            </div>
          )}
        </div>

        {/* Table */}
        <div className="overflow-x-auto">
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Data/Ora
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Utente
                </th>
                {isHost && (
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Tenant
                  </th>
                )}
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Azione
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Risorsa
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Dettagli
                </th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {isLoading ? (
                <tr>
                  <td colSpan={isHost ? 6 : 5} className="px-6 py-12 text-center">
                    <div className="flex justify-center">
                      <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary-600"></div>
                    </div>
                    <p className="mt-2 text-gray-500">Caricamento...</p>
                  </td>
                </tr>
              ) : logs.length === 0 ? (
                <tr>
                  <td colSpan={isHost ? 6 : 5} className="px-6 py-12 text-center text-gray-500">
                    Nessun log trovato
                  </td>
                </tr>
              ) : (
                logs.map((log) => (
                  <tr
                    key={log.id}
                    className="hover:bg-gray-50 cursor-pointer"
                    onClick={() => setSelectedLog(log)}
                  >
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                      <div className="flex items-center space-x-2">
                        <ClockIcon className="h-4 w-4 text-gray-400" />
                        <span>{formatDate(log.created_at)}</span>
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="flex items-center space-x-2">
                        <UserIcon className="h-4 w-4 text-gray-400" />
                        <div>
                          <div className="text-sm font-medium text-gray-900">
                            {log.user?.name || 'Utente sconosciuto'}
                          </div>
                          <div className="text-xs text-gray-500">
                            {log.user?.email}
                          </div>
                        </div>
                      </div>
                    </td>
                    {isHost && (
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="flex items-center space-x-2">
                          <BuildingOfficeIcon className="h-4 w-4 text-gray-400" />
                          <span className="text-sm text-gray-900">
                            {log.tenant?.name || 'N/A'}
                          </span>
                        </div>
                      </td>
                    )}
                    <td className="px-6 py-4 whitespace-nowrap">
                      {getActionBadge(log.action)}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="flex items-center space-x-2">
                        <DocumentTextIcon className="h-4 w-4 text-gray-400" />
                        <div>
                          <div className="text-sm font-medium text-gray-900">
                            {getResourceLabel(log.resource_type)}
                          </div>
                          <div className="text-xs text-gray-500 font-mono">
                            {log.resource_id.substring(0, 8)}...
                          </div>
                        </div>
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-primary-600 hover:text-primary-900">
                      Visualizza
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>

        {/* Pagination */}
        {pagination && pagination.pages > 1 && (
          <div className="px-6 py-4 border-t border-gray-200 flex items-center justify-between">
            <div className="text-sm text-gray-500">
              Mostrando {(pagination.page - 1) * pagination.limit + 1} -{' '}
              {Math.min(pagination.page * pagination.limit, pagination.total)} di{' '}
              {pagination.total} risultati
            </div>
            <div className="flex items-center space-x-2">
              <button
                onClick={() => handleFilterChange('page', pagination.page - 1)}
                disabled={pagination.page === 1}
                className="p-2 rounded-lg border border-gray-300 disabled:opacity-50 disabled:cursor-not-allowed hover:bg-gray-50"
              >
                <ChevronLeftIcon className="h-5 w-5" />
              </button>
              <span className="text-sm text-gray-700">
                Pagina {pagination.page} di {pagination.pages}
              </span>
              <button
                onClick={() => handleFilterChange('page', pagination.page + 1)}
                disabled={pagination.page === pagination.pages}
                className="p-2 rounded-lg border border-gray-300 disabled:opacity-50 disabled:cursor-not-allowed hover:bg-gray-50"
              >
                <ChevronRightIcon className="h-5 w-5" />
              </button>
            </div>
          </div>
        )}
      </div>

      {/* Detail Modal */}
      {selectedLog && (
        <div className="fixed inset-0 z-50 overflow-y-auto">
          <div className="flex items-center justify-center min-h-screen px-4 pt-4 pb-20 text-center sm:p-0">
            <div
              className="fixed inset-0 bg-gray-500 bg-opacity-75 transition-opacity"
              onClick={() => setSelectedLog(null)}
            />

            <div className="relative inline-block align-bottom bg-white rounded-lg text-left overflow-hidden shadow-xl transform transition-all sm:my-8 sm:align-middle sm:max-w-lg sm:w-full">
              <div className="bg-white px-4 pt-5 pb-4 sm:p-6 sm:pb-4">
                <div className="flex justify-between items-start">
                  <h3 className="text-lg font-medium text-gray-900">
                    Dettaglio Log
                  </h3>
                  <button
                    onClick={() => setSelectedLog(null)}
                    className="text-gray-400 hover:text-gray-500"
                  >
                    <XMarkIcon className="h-6 w-6" />
                  </button>
                </div>

                <div className="mt-4 space-y-4">
                  {/* Basic info */}
                  <div className="grid grid-cols-2 gap-4 text-sm">
                    <div>
                      <span className="text-gray-500">Data/Ora:</span>
                      <p className="font-medium">{formatDate(selectedLog.created_at)}</p>
                    </div>
                    <div>
                      <span className="text-gray-500">Azione:</span>
                      <p className="mt-1">{getActionBadge(selectedLog.action)}</p>
                    </div>
                    <div>
                      <span className="text-gray-500">Utente:</span>
                      <p className="font-medium">{selectedLog.user?.name || 'N/A'}</p>
                      <p className="text-xs text-gray-400">{selectedLog.user?.email}</p>
                    </div>
                    <div>
                      <span className="text-gray-500">Risorsa:</span>
                      <p className="font-medium">{getResourceLabel(selectedLog.resource_type)}</p>
                      <p className="text-xs text-gray-400 font-mono">{selectedLog.resource_id}</p>
                    </div>
                  </div>

                  {/* Tenant info (for host) */}
                  {isHost && selectedLog.tenant && (
                    <div className="text-sm">
                      <span className="text-gray-500">Tenant:</span>
                      <p className="font-medium">{selectedLog.tenant.name}</p>
                    </div>
                  )}

                  {/* IP and User Agent */}
                  {(selectedLog.ip_address || selectedLog.user_agent) && (
                    <div className="pt-4 border-t border-gray-200">
                      <h4 className="text-sm font-medium text-gray-900 mb-2 flex items-center space-x-2">
                        <ComputerDesktopIcon className="h-4 w-4" />
                        <span>Informazioni Tecniche</span>
                      </h4>
                      {selectedLog.ip_address && (
                        <p className="text-sm text-gray-600">
                          <span className="text-gray-500">IP: </span>
                          <span className="font-mono">{selectedLog.ip_address}</span>
                        </p>
                      )}
                      {selectedLog.user_agent && (
                        <p className="text-sm text-gray-600 mt-1">
                          <span className="text-gray-500">User Agent: </span>
                          <span className="font-mono text-xs break-all">{selectedLog.user_agent}</span>
                        </p>
                      )}
                    </div>
                  )}

                  {/* Changes */}
                  {renderChanges(selectedLog.old_values, selectedLog.new_values)}
                </div>
              </div>

              <div className="bg-gray-50 px-4 py-3 sm:px-6 sm:flex sm:flex-row-reverse">
                <button
                  type="button"
                  onClick={() => setSelectedLog(null)}
                  className="w-full inline-flex justify-center rounded-md border border-gray-300 shadow-sm px-4 py-2 bg-white text-base font-medium text-gray-700 hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500 sm:w-auto sm:text-sm"
                >
                  Chiudi
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default AuditLogs;
